import { useMemo } from "react";
import PropTypes from "prop-types";
import { Select, Form } from "antd";
import styled from "styled-components";
import tokens from "../../theme/tokens";

/**
 * Optional subtype picker, shown beside the category selector.
 *
 * A subtype is a finer label BELOW the category — "bva_decision",
 * "rating_decision" — and it does NOT change where the document is
 * filed. Routing needs to send Board decisions and regional-office
 * rating decisions to different people, but both are "Agency
 * Decisions", so the category alone cannot separate them. Tagging a
 * gold standard with a subtype teaches the categorizer that split.
 *
 * Renders nothing when the backend serves no vocabulary, so a backend
 * predating subtypes shows no empty dropdown.
 *
 * Deliberately optional and never validated as required: the honest
 * answer for the overwhelming majority of documents is "none".
 */

const Hint = styled.div`
  margin-top: 4px;
  color: ${tokens.text.tertiary};
  font-size: 12px;
  font-family: ${tokens.typography.fontFamily};
`;

const NONE_VALUE = "";

/**
 * Filter sentinel for "carries no subtype yet". A real slug can't
 * collide: the backend vocabulary is lowercase identifiers.
 */
export const SUBTYPE_NONE = "__none__";

export const buildSubtypeOptions = (subtypes = [], { category } = {}) => {
  const source = Array.isArray(subtypes) ? subtypes : [];
  return source
    .map((entry) => {
      if (typeof entry === "string") {
        return { value: entry, label: entry, typicalCategories: [] };
      }
      if (!entry || typeof entry !== "object" || !entry.value) return null;
      return {
        value: String(entry.value),
        label: entry.label || String(entry.value),
        description: entry.description || "",
        typicalCategories: Array.isArray(entry.typical_categories)
          ? entry.typical_categories
          : [],
      };
    })
    .filter(Boolean)
    .map((opt) => {
      // Advisory only. A subtype is never hidden or blocked for an
      // unusual category — the curator is the authority here, and a
      // dropped label costs more than an odd-looking one.
      const unusual =
        category &&
        opt.typicalCategories.length > 0 &&
        !opt.typicalCategories.includes(category);
      return {
        ...opt,
        label: unusual ? `${opt.label} (unusual for ${category})` : opt.label,
      };
    });
};

const SubtypeSelect = ({
  subtypes = [],
  value = "",
  category = "",
  disabled = false,
  onChange,
  testId = "subtype-select",
}) => {
  const options = useMemo(
    () => buildSubtypeOptions(subtypes, { category }),
    [subtypes, category]
  );

  if (options.length === 0) return null;

  const selected = options.find((opt) => opt.value === value);

  return (
    <Form.Item label="Subtype (optional)">
      <Select
        allowClear
        placeholder="None — most documents have no subtype"
        value={value || undefined}
        onChange={(next) => onChange(next || NONE_VALUE)}
        disabled={disabled}
        showSearch
        optionFilterProp="label"
        options={options}
        data-testid={testId}
      />
      <Hint>
        {selected?.description ||
          "A finer label below the category, used for routing. It does not " +
            "change which folder the document is filed in."}
      </Hint>
    </Form.Item>
  );
};

SubtypeSelect.propTypes = {
  subtypes: PropTypes.array,
  value: PropTypes.string,
  category: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

export default SubtypeSelect;
